import { FormEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LegalHero, Lang, useLang, CONTACT_EMAIL } from './LegalLayout';

// 自前 API（管理画面の「ゲスト連絡」に届く）
const CONTACT_API = '/api/contact';

type Text = {
  title: string; lead: string; cardTitle: string; cardText: string;
  property: string; propertyAny: string; name: string; email: string; phone: string; reservation: string; category: string;
  categories: string[]; message: string; agree: [string, string, string];
  submit: string; sending: string; success: string; error: string; required: string;
};

const T: Record<Lang, Text> = {
  ja: {
    title: 'お問い合わせ',
    lead: 'ご予約、ご宿泊、施設に関するご質問など、お気軽にお問い合わせください。',
    cardTitle: 'お問い合わせ窓口',
    cardText: '内容を確認のうえ、通常2営業日以内にメールでご返信いたします。お急ぎの場合はメールでも承ります。',
    property: '施設名', propertyAny: '未定・その他',
    name: 'お名前', email: 'メールアドレス', phone: '電話番号', reservation: '予約番号（お持ちの場合）',
    category: 'カテゴリー', categories: ['ご予約について', 'ご宿泊・チェックインについて', 'キャンセル・変更について', 'お支払いについて', 'その他'],
    message: 'お問い合わせ内容',
    agree: ['', 'プライバシーポリシー', 'に同意のうえ送信してください。'],
    submit: '送信する', sending: '送信中...', success: '送信しました。ありがとうございます。',
    error: '送信に失敗しました。時間をおいてもう一度お試しください。', required: '必須項目を入力してください。',
  },
  en: {
    title: 'Contact',
    lead: 'Feel free to contact us with any questions about reservations, your stay or our facilities.',
    cardTitle: 'Contact Desk',
    cardText: 'We usually reply by email within 2 business days. You can also contact us directly by email.',
    property: 'Property', propertyAny: 'Not decided / Other',
    name: 'Name', email: 'Email', phone: 'Phone', reservation: 'Reservation number (if any)',
    category: 'Category', categories: ['Reservations', 'Stay / Check-in', 'Cancellation / Changes', 'Payment', 'Other'],
    message: 'Message',
    agree: ['By submitting, you agree to our ', 'Privacy Policy', '.'],
    submit: 'Send', sending: 'Sending...', success: 'Your message has been sent. Thank you.',
    error: 'Failed to send. Please try again later.', required: 'Please fill in the required fields.',
  },
  zh: {
    title: '联系我们',
    lead: '关于预订、住宿或设施的任何问题，欢迎随时咨询。',
    cardTitle: '咨询窗口',
    cardText: '我们通常会在2个工作日内通过邮件回复。也可直接发送邮件联系我们。',
    property: '设施名称', propertyAny: '未定・其他',
    name: '姓名', email: '电子邮箱', phone: '电话号码', reservation: '预订编号（如有）',
    category: '类别', categories: ['关于预订', '关于住宿・入住', '关于取消・变更', '关于支付', '其他'],
    message: '咨询内容',
    agree: ['提交即表示您同意本设施的', '隐私政策', '。'],
    submit: '提交', sending: '发送中...', success: '已发送，谢谢！',
    error: '发送失败，请稍后再试。', required: '请填写必填项。',
  },
};

function ContactPage() {
  const lang = useLang();
  const t = T[lang];
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [properties, setProperties] = useState<{ id: string; name: string }[]>([]);
  // マイページの「この予約について問い合わせる」から来た場合の事前入力
  const [params] = useSearchParams();
  const pre = {
    reservation: params.get('reservation') || '',
    email: params.get('email') || '',
    name: params.get('name') || '',
    property_id: params.get('property_id') || '',
    category: t.categories[Number(params.get('cat'))] || t.categories[0],
  };

  // 施設一覧（予約ページと同じ公開API）
  useEffect(() => {
    fetch('/api/properties')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setProperties(Array.isArray(list) ? list : []))
      .catch(() => setProperties([]));
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      setStatus('error');
      setMsg(t.required);
      return;
    }
    const fd = new FormData(form);
    const email = String(fd.get('email') || '');
    setStatus('sending');
    setMsg(t.sending);

    try {
      const res = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site: 'staydreamhotel',
          form: 'contact',
          subject: '',
          replyTo: email,
          pageUrl: window.location.href,
          _gotcha: String(fd.get('_gotcha') || ''),
          metadata: { language: lang, page: window.location.href, version: '1.0' },
          data: {
            property_id: fd.get('property_id') || '',
            name: fd.get('name') || '',
            email,
            phone: fd.get('phone') || '',
            reservation: fd.get('reservation') || '',
            category: fd.get('category') || '',
            message: fd.get('message') || '',
          },
        }),
      });
      let result: { success?: boolean; requestId?: string } = {};
      try { result = await res.json(); } catch { /* non-JSON */ }
      if (!res.ok || result.success === false) throw new Error(`HTTP_${res.status}`);
      setStatus('success');
      setMsg(t.success + (result.requestId ? ` (ID: ${result.requestId})` : ''));
      form.reset();
    } catch (err) {
      console.error('contact submit failed', err);
      setStatus('error');
      setMsg(t.error);
    }
  };

  return (
    <div className="legal-page">
      <LegalHero eyebrow="Contact" title={t.title} lead={t.lead} />
      <section className="legal-body">
        <div className="contact-grid">
          <div className="contact-card">
            <h2>{t.cardTitle}</h2>
            <p>{t.cardText}</p>
            <p>
              <strong>Stay Dream Hotel</strong><br />
              ✉ <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </div>

          <form className="contact-card contact-form" onSubmit={onSubmit} noValidate>
            <div>
              <label className="field-label" htmlFor="cf-property">{t.property}</label>
              <select className="field" id="cf-property" name="property_id" key={properties.length} defaultValue={properties.some((p) => p.id === pre.property_id) ? pre.property_id : ''}>
                <option value="">{t.propertyAny}</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="cf-name">{t.name}<span className="req">*</span></label>
              <input className="field" id="cf-name" name="name" required autoComplete="name" defaultValue={pre.name} />
            </div>
            <div>
              <label className="field-label" htmlFor="cf-email">{t.email}<span className="req">*</span></label>
              <input className="field" id="cf-email" name="email" type="email" required autoComplete="email" defaultValue={pre.email} />
            </div>
            <div>
              <label className="field-label" htmlFor="cf-phone">{t.phone}</label>
              <input className="field" id="cf-phone" name="phone" type="tel" autoComplete="tel" />
            </div>
            <div>
              <label className="field-label" htmlFor="cf-reservation">{t.reservation}</label>
              <input className="field" id="cf-reservation" name="reservation" defaultValue={pre.reservation} readOnly={!!pre.reservation} />
            </div>
            <div>
              <label className="field-label" htmlFor="cf-category">{t.category}</label>
              <select className="field" id="cf-category" name="category" defaultValue={pre.category}>
                {t.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="cf-message">{t.message}<span className="req">*</span></label>
              <textarea className="field" id="cf-message" name="message" rows={6} required />
            </div>
            {/* ボット対策（人間は入力しない隠しフィールド） */}
            <div className="honeypot" aria-hidden="true">
              <input name="_gotcha" type="text" tabIndex={-1} autoComplete="off" />
            </div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
              {t.agree[0]}<Link to="/privacy">{t.agree[1]}</Link>{t.agree[2]}
            </p>
            <div>
              <button className="btn-primary" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? t.sending : t.submit}
              </button>
            </div>
            {status !== 'idle' && (
              <p className={`form-message ${status}`} role="status" aria-live="polite">{msg}</p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
